/**
	* Feature tools
	* Handles: formatting, adding/removing, saving, intersection, focussing features
	* @module tools/feature
*/

/// Imports

// OpenLayers
import OlMap from "ol/Map";
import Feature from "ol/Feature";
import Geometry from "ol/geom/Geometry";
import Polygon from "ol/geom/Polygon";
import MultiPolygon from "ol/geom/MultiPolygon";
import Point from "ol/geom/Point";
import Circle from 'ol/geom/Circle';
import WKT from "ol/format/WKT";
import GeoJSON from "ol/format/GeoJSON";
import VectorSource from "ol/source/Vector";
import Collection from "ol/Collection";
import { GeoJSONFeature } from "ol/format/GeoJSON";
import { getArea, getLength } from "ol/sphere";
import { toStringXY } from "ol/coordinate";
import { Units } from "ol/control/ScaleLine";
import { 
	equals as extentEquals, 
	extend, 
	createEmpty as createEmptyExtent, 
	getCenter, 
	getHeight, 
	getWidth 
} from "ol/extent";
import { transform } from "ol/proj";

// Turf
import turf from "turf";
import intersect from "@turf/intersect";
import difference from "@turf/difference";
import unkinkPolygon from "@turf/unkink-polygon";

// Tools
import { roundtodp } from "lib/utilities/maths";
import { fetchRequest } from "lib/utilities/network";
import { wordWrapString } from 'lib/utilities/misc';

// Lib
import DrawEvent from "lib/enums/drawevent";
import FeatureVariation from "lib/classes/featurevariation";
import ChangelogMode from 'lib/enums/changelogmode';
import { changelog } from 'lib/feature/changelog'; 

// Lang
import { getLocaleString } from "lib/localization/vendor";

/**
	* Readonly feature properties
	* @remarks
	* These attribute names are none-editable by the feature editor
*/
export const readonlyAttributes:Array<string> = ["variation-id", "interaction-name", "type", "length", "size-info", "area", "area-m2", "area-ha", "area-sqkm", "feature-id", "editable", "uuid", "origin", "annotation", "radius", "changelog"];

/**
	* Format feature
	* 
	* @param feature	- Feature to format
	* 
	* @remarks
	* Formats polygon/point respectively
*/
export function formatFeature(
	feature:Feature<Geometry>, 
	callback:Function = null, 
	changelogMode:ChangelogMode = ChangelogMode.None
) {
	const geometry:Geometry = feature.getGeometry();
	
	// Check if point is actually a circle and convert
	if(geometry.getType() == 'Point' && feature.get('radius')) {
		feature.setGeometry(
			new Circle((geometry as Point).getCoordinates(), feature.get('radius'))
		);
	}
	
	switch(geometry.getType()) {
		case "Polygon":
		case "MultiPolygon":
			formatPolygon(feature);
		break;
		case "Point":
			formatPoint(feature);
		break;
		case "LineString":
			formatLineString(feature);
		break;
		case "Circle":
			const circle:Circle = feature.getGeometry() as Circle;
			
			feature.set('radius', circle.getRadius());
		break;
	}
	
	if(changelogMode == ChangelogMode.Created || changelogMode == ChangelogMode.Modified)
		changelog(feature, changelogMode);
	
	callback?.(feature);
}

/**
	* Format polygon
	* 
	* @param feature	- Feature to format
	* 
	* @remarks
	* Assigns attributes to feature
*/
export function formatPolygon(feature:Feature<Geometry>) {
	const geom:Geometry = feature.get("geometry");
	
	feature.set("type", "polygon");
	feature.set("feature-id", feature.getId());
	feature.set("editable", true);
	feature.set("uuid", _config.UUID);
	
	if(!feature.get('annotation')) {
		feature.set("length", getLength(geom));
		feature.set("size-info", formatArea(geom));
		feature.set("area-m2", getArea(geom));
		feature.set("area-ha", getPolygonHa(geom));
		feature.set("area-sqkm", getPolygonSqkm(geom));
		feature.unset("area"); // Obsolete
	}
}

/**
	* Format point
	* 
	* @param feature	- Feature to format
	* 
	* @remarks
	* Assigns attributes to feature
*/
export function formatPoint(feature:Feature<Geometry>) {
	feature.set("type", "point"); 
	feature.set("length", 0);
	feature.set("feature-id", feature.getId());
	feature.set("editable", true);
	feature.set("uuid", _config.UUID);
}

/**
	* Format line string
	*
	* @param feature - Feature to format
	* 
	* @remarks
	* Assigns attributes to a line string feature
*/
export function formatLineString(feature:Feature<Geometry>) {
	const geom:Geometry = feature.get("geometry");
	
	feature.set("type", "linestring"); 
	feature.set("feature-id", feature.getId());
	feature.set("editable", true);
	feature.set("uuid", _config.UUID);
	
	if(!feature.get('annotation')) {
		feature.set("size-info", formatLength(geom));
		feature.set("length", getLength(geom));
	}
}

/**
	* Clear size info
	* 
	* @param feature	- Feature to format
	* 
	* @remarks
	* Removes content of area info box while feature is being modified.
*/
export function clearSizeInfo(feature:Feature<Geometry>) {
	feature.set("size-info", "");
}

/**
	* Collapse feature
	* 
	* @param feature	- Feature to format
	* 
	* @remarks
	* Because features don't disappear from map even when removed from
	* drawing source, you need to change their geometry so they disappear.
*/
export function collapseFeature(feature:Feature<Geometry>) {
	// Hack to make a feature invisible by setting all its geo points to zero
	feature.setGeometry( new Point([]) );
	feature.set("collapsed", true);
}

/**
	* Remove feature from source
	* 
	* @param feature		-	Feature to remove
	* @param vectorSource	-	Vector source to remove feature from
	* @param callback		-	Method to call when feature is removed
	* 
	* @remarks
	* This does not affect local storage of features. deleteFeature(...) does that.
*/
export function removeFeatureFromSource(feature:Feature<Geometry>, vectorSource:VectorSource, callback:Function = null, removeFromSource:boolean = false) {
	collapseFeature(feature);
	
	if(removeFromSource)
		vectorSource.removeFeature(feature);
	
	callback?.(feature);
}

/**
	* Delete feature from local storage & vector source.
	* 
	* @param feature		-	Feature to remove
	* @param vectorSource	-	Vector source to remove feature from
	* @param callback		-	Method to call when feature is removed
*/
export function deleteFeature(feature:Feature<Geometry>, vectorSource:VectorSource) {
	removeFeatureFromSource(feature, vectorSource);
}

/**
	* Return details of polygon area
	* 
	* @param polygon	-	Polygon to measure
*/
export function formatArea(polygon:Geometry):string {
	const unit:Units = _ui.getScaleLineUnits();
	const area:number = getArea(polygon);
	const sqkm:number = getPolygonSqkm(polygon);
	
	const measures:Array<string> = [];
	
	switch(unit) {
		case "metric":
		case "degrees": // unsupported
			const ha:number = getPolygonHa(polygon);
			
			measures.push(`${roundtodp(ha, 2)} ha`);
			
			if(sqkm > 0.01)
				measures.push(`${roundtodp(sqkm, 2)} km²`);
			else
				measures.push(`${roundtodp(area, 2)} m²`);
		break;
		case "us":
		case "imperial":
			const acres:number = sqkm * 247.1;
			const sqmi:number = sqkm / 2.59;
			
			measures.push(`${roundtodp(acres, 2)} acres`);
			measures.push(`${roundtodp(sqmi, 2)} mi²`);
		break;
		case "nautical":
			const sqnm:number = sqkm / 3.43;
			
			measures.push(`${roundtodp(sqnm, 2)} nm²`);
		break;
	}
	
	return measures.join("\n");
}

/**
	* Return length of line string
	* 
	* @param line - Line string geometry
*/
export function formatLength(line:Geometry):string {
	const unit:Units = _ui.getScaleLineUnits();
	const length:number = getLength(line);
	
	const measures:Array<string> = [];
	
	switch(unit) {
		case "metric":
		case "degrees": // unsupported
			if (length > 1000)
				measures.push(`${(roundtodp(Math.round((length / 1000) * 100) / 100, 2))} km`);
			else
				measures.push(`${(roundtodp(Math.round(length * 100) / 100, 1))} m`);
				
		break;
		case "us":
		case "imperial":
			const feet:number = length * 3.281;
			
			measures.push(`${(roundtodp(feet, 1))} ft`);
		break;
		case "nautical":
			const nautical:number = length / 1852;
			
			measures.push(`${(roundtodp(nautical, 2))} nm`);
		break;
	}
	
	return measures.join("\n");
}

/**
	* Clean multi-polygon
	* 
	* @param {Feature<Geometry} feature - Multi-polygon feature
	* 
	* @returns {Feature<Geometry} Cleaned multi-polygon feature
*/
export function cleanMultiPolygon(feature:Feature<Geometry>):Feature<Geometry> {
	const multiPolygon:MultiPolygon = feature.getGeometry() as MultiPolygon;
	const polygons:Polygon[] = multiPolygon.getPolygons();
	
	// Clone the feature to make a new one with cleaned geometry
	const newFeature:Feature<Geometry> = feature.clone();
	const newMultiPolygon:MultiPolygon = new MultiPolygon([]);
	
	let polygonsRemoved:number = 0;
	
	polygons.forEach((polygon:Polygon) => {
		const area:number = getArea(polygon);
		
		if(area >= _config.minimumPolygonArea)
			newMultiPolygon.appendPolygon(polygon);
		else 
			polygonsRemoved++;
	});
	
	if(polygonsRemoved > 0)
		console.log(`Cleaned ${polygonsRemoved} polygon(s) from multi-polygon id ${feature.get("feature-id")}`);
	
	newFeature.setGeometry(newMultiPolygon);
	
	return newFeature;
}

/**
	* Return details of polygon area (hectares)
	* 
	* @param polygon	-	Polygon to measure
*/
export function getPolygonHa(polygon:Geometry):number {
	const area:number = getArea(polygon);
	return area / 10000;
}

/**
	* Return details of polygon area (square km)
	* 
	* @param polygon	-	Polygon to measure
*/
export function getPolygonSqkm(polygon:Geometry):number {
	const hectares:number = getPolygonHa(polygon);
	return hectares / 100;
}

/**
	* Describe feature type with locale string
	* 
	* @param featureType	-	Type of feature (point|polygon)
*/
export function describeType(featureType:string):string {
	switch(featureType) {
		case "point": return getLocaleString("accesspoint");
		case "polygon": return getLocaleString("polygon");
	}
}

/**
	* Get next feature id
	* 
	* @param vectorSource	-	Vector source to check
	* 
	* @remarks
	* Its essential to get a unique ID
*/
export function getNextFeatureId(vectorSource:VectorSource):number {
	const allFeatures:Feature<Geometry>[] = vectorSource.getFeatures();
	
	let id:number = allFeatures.length + 1;
	
	while(idInUse(id, vectorSource))
		id++;
	
	return id;
}

/**
	* Is ID in use?
	* 
	* @param id				-	ID to check
	* @param vectorSource	-	Vector source to check
*/
export function idInUse(id:number, vectorSource:VectorSource):boolean {
	const allFeatures:Feature<Geometry>[] = vectorSource.getFeatures();
	
	for(let feature of allFeatures) {
		if(feature.get("feature-id") == id)
			return true;
	}
	
	return false;
}

/**
	* Save features to local source
	* 
	* @param vectorSource	-	Vector source to read features from
	* 
	* @remarks
	* The features read from the vector source are actually the state.features 
	* map from views/DrawTool
*/
export function saveFeaturesLocal(vectorSource:VectorSource) {
	const format:GeoJSON = vectorSource.get("feature-format");
	const features:Feature<Geometry>[] = vectorSource.getFeatures();
	const jsonData:string = format.writeFeatures(features);
	
	localStorage.setItem(_config.getLocalStorageId(), jsonData);
}

/**
	* Save features remote
	* 
	* @param {VectorSource} vectorSource - Vector source to save from
	*
	* @remarks
	* Method will send a post request to server with features as GeoJSON
*/
export function saveFeaturesRemote(map:OlMap, vectorSource:VectorSource) {
	const mapConfig:any = map.get("opus-config");
	const client:string = mapConfig.client;
	const mapId:number = mapConfig.id;
	const prependUUID:string = vectorSource.get('puuid') ?? '';
	const UUID:string = (prependUUID != '' ? `${prependUUID}-` : '') + _config.UUID;
	
	const url:string = `https://${client}.opus4.co.uk/api/v2/call-for-sites/${mapId}/${UUID}/`;
	
	const format:GeoJSON = vectorSource.get("feature-format");
	const features:Feature<Geometry>[] = vectorSource.getFeatures();
	
	// Convert each feature's geometry to proper GeoJSON format
	const geoJSONFeatures:any[] = features
	.filter((feature:Feature<Geometry>) => {
		
		// Skip any features with empty geometry
		if(getCoordinatesCount(feature) === 0)
			return false;
		
		return true;
	})
	.map((feature:Feature<Geometry>) => {
		
		// Convert circles to points
		if(feature.getGeometry().getType() == 'Circle') {
			const center:number[] = (feature.getGeometry() as Circle).getCenter();
			const point:Point = new Point(center);
			
			feature = feature.clone();
			feature.setGeometry(point);
		}
		
		return format.writeFeatureObject(feature);
	});
	
	const finalGeoJSON:any = {
		type: 'FeatureCollection',
		features: geoJSONFeatures,
	};
	const jsonData:string = JSON.stringify(finalGeoJSON, null, 2);
	
	const options:any = {
		method: "POST",
		headers: new Headers({
			"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
		}),
		body: jsonData
	}
	
	fetchRequest(url, options);
}

/**
	* Get source features as JSON data
	* 
	* @param {VectorSource} source - Source to read features from
	* @param {string} epsgCodeView - View projection code (defaults to EPSG:3857)
	* 
	* @returns {string} json data
*/
export function getSourceFeaturesJson(source:VectorSource, epsgCodeView:string = "EPSG:3857", filterFunction:Function) {
	const format:GeoJSON = new GeoJSON({ featureProjection: epsgCodeView });
	const features:Feature<Geometry>[] = source.getFeatures();
	const restoreMethods:Function[] = filterFunction?.(features);
	
	const json:string = format.writeFeatures(features);
	
	restoreMethods?.forEach((restore:Function) => {
		restore();
	});
	
	return json;
}

/**
	* Polygon intersects
	* 
	* @param featureA		-	Feature to check against
	* @param vectorSource	-	Vector source to read features from
	* 
	* @remarks
	* This method reads features by the native OL getFeatures method.
	* It works very well with polygons and is preferred over the
	* `featureIntersects()` method.
*/
export function polygonIntersects(featureA:Feature<Geometry>, vectorSource:VectorSource):Feature<Geometry> {
	if(featureA.getGeometry().getType() != "Polygon") return;
	
	const features:Feature<Geometry>[] = vectorSource.getFeatures();
	const format:GeoJSON = vectorSource.get("feature-format");
	const geoJsonFeatureA:GeoJSONFeature = format.writeFeatureObject(featureA);
	
	for(let featureB of features) {
		if(featureB.get("collapsed")) continue;
		
		const typeB:string = featureB.getGeometry().getType();
		
		if(typeB != "Polygon" && typeB != "MultiPolygon") continue;
		
		const geoJsonFeatureB:GeoJSONFeature = format.writeFeatureObject(featureB);
		const intersection = intersect(geoJsonFeatureA, geoJsonFeatureB);
		
		if(intersection)
			return featureB;
	}
	
	return null;
}

/**
	* Polygon intersects (returns array)
	* 
	* @param featureA		-	Feature to check against
	* @param vectorSource	-	Vector source to read features from
	* 
	* @remarks
	* This method reads features by the native OL getFeatures method.
	* It works very well with polygons and is preferred over the
	* `featureIntersects()` method.
*/
export function polygonIntersections(featureA:Feature<Geometry>, vectorSource:VectorSource):Feature<Geometry>[] {
	if(featureA.getGeometry().getType() != "Polygon") return;
	
	const features:Feature<Geometry>[] = vectorSource.getFeatures();
	const format:GeoJSON = vectorSource.get("feature-format");
	const geoJsonFeatureA:GeoJSONFeature = format.writeFeatureObject(featureA);
	
	const intersections:Feature<Geometry>[] = [];
	
	for(let featureB of features) {
		if(featureB.get("collapsed")) continue;
		
		const typeB:string = featureB.getGeometry().getType();
		
		if(typeB != "Polygon" && typeB != "MultiPolygon") continue;
		
		const geoJsonFeatureB:GeoJSONFeature = format.writeFeatureObject(featureB);
		const intersection = intersect(geoJsonFeatureA, geoJsonFeatureB);
		
		if(intersection)
			intersections.push(featureB);
	}
	
	return intersections;
}

/**
	* Feature intersects
	* 
	* @param feature		-	Feature to save
	* @param vectorSource	-	Vector source to save from
	* @param typeToType		-	Allowed feature type to check against (point|polygon)
	* 
	* @remarks
	* This method is only used for point features because polygon 
	* intersection doesn't work well. 
	* 
	* See under `insertFeature()` in views/DrawTool.
*/
export function featureInsersects(featureA:Feature<Geometry>, vectorSource:VectorSource, typeToType:string = ""):Feature<Geometry> {
	const features:Feature<Geometry>[] = vectorSource.getFeatures()
	const format:GeoJSON = vectorSource.get("feature-format");
	const geoJsonFeatureA:GeoJSONFeature = format.writeFeatureObject(featureA);
	
	for(let featureB of features) {
		if(featureB.get("collapsed")) continue;
		if(featureB.getGeometry().getType() != typeToType && typeToType) continue;
		
		const geoJsonFeatureB:GeoJSONFeature = format.writeFeatureObject(featureB);
		
		if(turf.intersect(geoJsonFeatureA, geoJsonFeatureB))
			return featureB;
	}
	
	return null;
}

/**
	* Focus map on features
	* 
	* @param map		-	Map to focus
	* @param features	-	Feature array to calculate extents around
*/
export function focusFeatures(map:OlMap, features:Feature<Geometry>[]) {
	let selectedCount:number = features.length;
	
	if(selectedCount < 1) return;
	
	let extent = createEmptyExtent();
	
	features.forEach((feature:Feature<Geometry>) => {
		extend(extent, feature.getGeometry().getExtent());
	});
	
	_ui.tooltip.hide();
	
	const p:number = _config.focusFeaturePadding;
	
	map.getView().setCenter(map.getView().getCenter()); // to cancel an existing animation on the map (ol-hashed)
	map.getView().fit(extent, {padding: [p, p, p, p], duration: _config.slideTranSpeed});
}

/**
	* Feature draw event
	* 
	* @param {DrawEvent} drawEvent - The draw event type
	* @param {string} featureType - Type of feature
*/
export function onFeatureDrawEvent(drawEvent:DrawEvent, featureType:string) {
	const callback:any = window[_config.featureDrawnCallback as any];
	
	callback?.(_config.UUID, drawEvent, featureType);
}

/**
	* Should save feature?
	* 
	* @param {Feature<Geometry>} feature - Feature to save
	* 
	* @returns {boolean}
*/
export function shouldSave(feature:Feature<Geometry>):boolean {
	return feature.get("origin") != 'measurement' && !feature.get("rasterimg");
}

/**
	* Should modify feature?
	* 
	* @param {Feature<Geometry>} feature - Feature to modify
	* 
	* @returns {boolean}
*/
export function shouldModify(feature:Feature<Geometry>):boolean {
	return !feature.get("rasterimg");
}

/**
	* Get center of geometry
	* 
	* @param {Geometry} geometry
	* 
	* @returns {any} object of co-ords, center, radius and sqr dist
*/
export function calculateCenter(geometry:any):any {
	let center:any, coordinates:any, minRadius:any;
	
	const type = geometry.getType();
	
	if (type === 'Polygon') {
		let x = 0;
		let y = 0;
		let i = 0;
		
		coordinates = geometry.getCoordinates()[0].slice(1);
		
		coordinates.forEach((coordinate:any) => {
			x += coordinate[0];
			y += coordinate[1];
			i++;
		});
		
		center = [x / i, y / i];
	} else if (type === 'LineString') {
		center = geometry.getCoordinateAt(0.5);
		coordinates = geometry.getCoordinates();
	} else {
		center = getCenter(geometry.getExtent());
	}
	
	let sqDistances;
	
	if (coordinates) {
		sqDistances = coordinates.map((coordinate:any) => {
			const dx = coordinate[0] - center[0];
			const dy = coordinate[1] - center[1];
			
			return dx * dx + dy * dy;
		});
		
		minRadius = Math.sqrt(Math.max.apply(Math, sqDistances)) / 3;
	} else {
		minRadius = Math.max(
			getWidth(geometry.getExtent()),
			getHeight(geometry.getExtent())
		) / 3;
	}
	
	return {
		center: center,
		coordinates: coordinates,
		minRadius: minRadius,
		sqDistances: sqDistances,
	};
}

/**
	* Get features with filter function
	* 
	* @param {VectorSource} vectorSource - Vector source to get features from
*/
export function getFeatures(vectorSource:VectorSource) {
	const features:Feature<Geometry>[] = vectorSource.getFeatures();
	const featuresFiltered:Feature<Geometry>[] = [];
	
	features.forEach((feature:Feature<Geometry>) => {
		if(!feature.get("collapsed") && (feature.get("filter-show") || feature.get("filter-show") === undefined))
			featuresFiltered.push(feature);
	});
	
	return featuresFiltered;
}

/**
	* Get feature label
	* 
	* @returns {string} The label
*/
export function getFeatureLabel(feature:Feature<Geometry>, index:number = 0):string {
	const variation:FeatureVariation = getFeatureVariation(feature.get("variation-id"), feature.get("interaction-name"));
	const isMeasurement:boolean = feature.get("origin") == 'measurement';
	const isSketch:boolean = feature.get("sketch");
	const isDrawn:boolean = !!feature.get("origin");
	const type:string = feature.getGeometry().getType();
	
	let text:string = "";
	let variationLabel:string = "";
	
	if((isSketch || isDrawn) && !variation?.hideLabel)
		variationLabel = (variation?.name || "");
		
	if(type == "MultiPolygon") {
		const multiPolygon:MultiPolygon = feature.getGeometry() as MultiPolygon;
		const total:number = multiPolygon.getPolygons().length;
		const polygon:Polygon = multiPolygon.getPolygon(index);
		
		text += `${variationLabel || getLocaleString("polygon")} ${index+1}/${total}\n`;
		text += formatArea(polygon);
	}
	
	if(feature.get('annotation'))
		text = wordWrapString(feature.get('Label'));
	else
	if(feature.get("get-zoom")?.() < 16 && !feature.get("inprogress"))
		text = "";
	else
	if(text == "") {
		text += variationLabel;
		
		if(feature.get("size-info"))
			text += (text ? "\n" : "") + feature.get("size-info");
	}
	
	return text;
}

/**
	* Get feature variation
	* 
	* @returns {FeatureVariation} The feature variation
*/
export function getFeatureVariation(variationId:string, typeId:string):FeatureVariation {
	const variations:Map<string, FeatureVariation[]> = _config.getFeatureVariations();
	const types:FeatureVariation[] = variations.get(typeId);
	
	if(!types) return;
	
	for(let type of types) {
		if(type.id == variationId)
			return type;
	}
}

/**
	Get lat long coords of a feature's center
	@returns {Array<number>} co-ordinates of the feature in WGS 84 projection
*/
export function getCenterCoords(feature:Feature<Geometry>, epsgCodeView:string = "EPSG:3857"):Array<number> {
	const geometry:Geometry = feature.getGeometry();
	
	return transform(
		getCenter(geometry.getExtent()),
		epsgCodeView,
		"EPSG:4326" // lat long
	);
}

/**
	* Get if polygon is self intersecting
	* 
	* @param {Feature<Geometry>} feature
	* @returns {boolean} Is kinked
*/
export function getIsSelfIntersecting(feature:Feature<Geometry>):boolean {
	const polygon:Polygon = feature.getGeometry() as Polygon;
	const coords:any = polygon.getCoordinates();
	const kinkedPolygon:any = turf.polygon(coords);
	const unkinkedPolygons:any = unkinkPolygon(kinkedPolygon);
	
	return unkinkedPolygons.features.length > 1;
}

/**
	* Get feature's coordinates size
	*
	* @param {Feature<Geometry>} feature
	* @returns {number} Co-ordinates count
*/
export function getCoordinatesCount(feature:Feature<Geometry>) {
	const geometry:any = feature.getGeometry();
	const coords:any = geometry.getCoordinates?.();
	
	return coords?.length ?? 0;
}

/**
	* Parse site 
*/
export function parseWKTFeature(wktData:string, mapConfig:any): Feature<Geometry> {
	const format:WKT = new WKT();
	
	return format.readFeature(wktData, {
		dataProjection: mapConfig.epsgCode,
		featureProjection: mapConfig.epsgCodeView,
	});
}